/**
 * Public Check-in Scan Page – UC-003
 * Accessible without login: /checkin/:token
 * Used by cruise/airline/hotel staff to read the traveler's health data.
 */
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import api from "@/services/api/apiClient";
import {
  AlertTriangle,
  Building,
  CheckCircle,
  Clock,
  Droplets,
  Heart,
  Info,
  Mail,
  Phone,
  Pill,
  Shield,
  Syringe,
  User,
  XCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  EmergencyAllergy,
  EmergencyCondition,
  EmergencyContact,
  EmergencyMedication,
} from "@/types/emergency";

interface CheckinPreviewData {
  operatorType: string;
  operatorName?: string;
  bookingRef?: string;
  expiresAt: string;
  dataScope: string[];
  requiresIdentity?: boolean;
}

interface CheckinData extends CheckinPreviewData {
  name: string;
  bloodType?: string;
  dateOfBirth?: string;
  // The check-in route passes the priority-1 emergency aggregate straight
  // through, only renaming the keys. The field names below were previously
  // invented (`substance`, `severity`, `name`) and rendered blank for every
  // entry on a page a first responder reads.
  emergency?: {
    allergies: EmergencyAllergy[];
    criticalMedications: EmergencyMedication[];
    medicalConditions: EmergencyCondition[];
    emergencyContacts: EmergencyContact[];
  };
}

const CheckinScanPage: React.FC = () => {
  const fmt = useLocaleFormat();
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<CheckinPreviewData | null>(null);
  const [data, setData] = useState<CheckinData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [accessForm, setAccessForm] = useState({
    fullName: "",
    organization: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (!token) return;
    api
      .get(`/checkin/${token}`)
      .then((res: { data: { data: CheckinPreviewData | CheckinData } }) => {
        const payload = res.data.data;
        if ((payload as CheckinData).name) {
          setData(payload as CheckinData);
          setPreview(payload as CheckinPreviewData);
        } else {
          setPreview(payload as CheckinPreviewData);
        }
      })
      .catch(
        (err: { response?: { data?: { error?: { message?: string } } } }) => {
          const msg = err?.response?.data?.error?.message;
          setError(
            msg === "Token not found or expired"
              ? "This QR code is invalid or has expired."
              : "Failed to load check-in data.",
          );
        },
      )
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const expiresAt = data?.expiresAt ?? preview?.expiresAt;
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft("Abgelaufen");
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${h}h ${m}min`);
    };
    update();
    const iv = setInterval(update, 30000);
    return () => clearInterval(iv);
  }, [data?.expiresAt, preview?.expiresAt]);

  const handleAccessRequest = async () => {
    if (!token) return;

    if (!accessForm.fullName.trim() || !accessForm.organization.trim()) {
      setError("Please provide your name and organization.");
      return;
    }

    if (!consentAccepted) {
      setError("Please confirm the data access notice.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const res = await api.post(`/checkin/${token}/access`, {
        fullName: accessForm.fullName,
        organization: accessForm.organization,
        email: accessForm.email || undefined,
        phone: accessForm.phone || undefined,
        consentAccepted: true,
      });

      setData(res.data.data);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message;
      setError(msg || "Failed to load check-in data.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-600 text-sm">Loading health data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-md p-8">
          <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Access unavailable
          </h1>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-400 mt-4">
            Please request a new QR code from the airline, hotel, or cruise
            operator.
          </p>
        </div>
      </div>
    );
  }

  if (!data && preview) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-blue-700 text-white px-4 py-5">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-3 mb-1">
              <Shield className="w-6 h-6" />
              <span className="text-sm font-semibold tracking-wide uppercase opacity-90">
                HealthPass Check-in
              </span>
            </div>
            <h1 className="text-2xl font-bold">Operator verification</h1>
            {preview.operatorName && (
              <p className="text-blue-200 text-sm mt-1">
                {preview.operatorName}
                {preview.bookingRef && ` · ${preview.bookingRef}`}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Clock className="w-3.5 h-3.5 text-blue-300" />
              <span className="text-xs text-blue-200">
                Valid for {timeLeft}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-4">
            <div>
              <h2 className="font-semibold text-gray-900 mb-1">
                Please identify yourself before access
              </h2>
              <p className="text-sm text-gray-600">
                The traveler created this QR code voluntarily for check-in. Your
                access will be logged.
              </p>
            </div>

            <div>
              <label
                htmlFor="checkin-fullname"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Your name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  id="checkin-fullname"
                  value={accessForm.fullName}
                  onChange={(e) =>
                    setAccessForm({ ...accessForm, fullName: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 text-sm"
                  placeholder="Full name"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="checkin-organization"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Organization <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  id="checkin-organization"
                  value={accessForm.organization}
                  onChange={(e) =>
                    setAccessForm({
                      ...accessForm,
                      organization: e.target.value,
                    })
                  }
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 text-sm"
                  placeholder="Airline, Hotel, Cruise Line"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="checkin-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  id="checkin-email"
                  value={accessForm.email}
                  onChange={(e) =>
                    setAccessForm({ ...accessForm, email: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 text-sm"
                  placeholder="name@company.com"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-amber-800">
                <input
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I confirm that this access is only for the current travel
                  check-in and may be logged.
                </span>
              </label>
            </div>

            <button
              onClick={handleAccessRequest}
              disabled={submitting || !consentAccepted}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
            >
              {submitting ? "Loading data…" : "View HEALTHPASS data"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const hasEmergency = data.dataScope.includes("emergency") && data.emergency;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-700 text-white px-4 py-5">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <Shield className="w-6 h-6" />
            <span className="text-sm font-semibold tracking-wide uppercase opacity-90">
              HealthPass Check-in
            </span>
          </div>
          <h1 className="text-2xl font-bold">{data.name}</h1>
          {data.operatorName && (
            <p className="text-blue-200 text-sm mt-1">
              {data.operatorName}
              {data.bookingRef && ` · ${data.bookingRef}`}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Clock className="w-3.5 h-3.5 text-blue-300" />
            <span className="text-xs text-blue-200">Valid for {timeLeft}</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Base data */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            Personal details
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {data.bloodType && (
              <div className="bg-red-50 rounded-lg p-3 flex items-center gap-2">
                <Droplets className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-xs text-red-600 font-medium">Blood type</p>
                  <p className="font-bold text-red-800 text-lg">
                    {data.bloodType}
                  </p>
                </div>
              </div>
            )}
            {data.dateOfBirth && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium">
                  Date of birth
                </p>
                <p className="font-semibold text-gray-900">
                  {fmt.date(data.dateOfBirth)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Emergency data */}
        {hasEmergency && data.emergency && (
          <>
            {/* Allergies */}
            {data.emergency.allergies.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
                <h2 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Allergies
                </h2>
                <div className="space-y-2">
                  {data.emergency.allergies.map((a, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2"
                    >
                      <span className="font-medium text-red-900 text-sm">
                        {a.allergen}
                      </span>
                      {a.criticality && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.criticality === "high" ? "bg-red-200 text-red-800" : "bg-orange-100 text-orange-800"}`}
                        >
                          {a.criticality}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medications */}
            {data.emergency.criticalMedications.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100">
                <h2 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  Critical medications
                </h2>
                <div className="space-y-2">
                  {data.emergency.criticalMedications.map((m, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-purple-50 rounded-lg px-3 py-2"
                    >
                      <span className="font-medium text-purple-900 text-sm">
                        {m.medicationName}
                      </span>
                      {m.dosage && (
                        <span className="text-xs text-purple-600">
                          {m.dosage}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conditions */}
            {data.emergency.medicalConditions.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
                <h2 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Conditions
                </h2>
                <div className="flex flex-wrap gap-2">
                  {data.emergency.medicalConditions.map((c, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-blue-50 text-blue-800 rounded-full text-sm font-medium border border-blue-100"
                    >
                      {c.condition}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Emergency contacts */}
            {data.emergency.emergencyContacts.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
                <h2 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Emergency contacts
                </h2>
                <div className="space-y-2">
                  {data.emergency.emergencyContacts.map((c, i) => (
                    <div key={i} className="bg-green-50 rounded-lg px-3 py-2">
                      <p className="font-medium text-green-900 text-sm">
                        {c.name}{" "}
                        <span className="text-green-600 font-normal">
                          ({c.relationship})
                        </span>
                      </p>
                      <a
                        href={`tel:${c.phone}`}
                        className="text-green-700 text-sm font-mono hover:underline"
                      >
                        {c.phone}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Vaccination scope note */}
        {data.dataScope.includes("vaccinations") && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-teal-100 flex items-start gap-3">
            <Syringe className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-teal-800 text-sm">
                Vaccination status shared
              </p>
              <p className="text-xs text-teal-600 mt-0.5">
                The full vaccination status is available in the traveler's
                HealthPass for authorized personnel.
              </p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-500">
            These data were shared voluntarily by the traveler. All access is
            logged and visible to the account holder. Privacy handling follows
            the GDPR.
          </p>
        </div>

        <div className="text-center py-2">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            Verified by HealthPass · {fmt.date(new Date())}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckinScanPage;
